import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasValue, styles } from "@repo/helpers";
import { Row } from "@repo/ui";
import { useCallback, useMemo } from "react";

import { useRepEnrollmentQuery } from "..";
import { useUserContext } from "../../../../contexts/userContext";
import { useSettingsQuery } from "../../../../helpers/queryHooks";
import MapSale from "../../../../models/mapSale";
import style from "./style.module.scss";

interface Props {
	selected: number;
	hovered: number;
	onHover: (id: number) => void;
	onSelect: (id: number) => void;
}

export function RecentEnrollmentsList(props: Props) {
	const { selected } = props;
	const { data } = useRepEnrollmentQuery();
	const { user } = useUserContext();
	const [settings] = useSettingsQuery();
	const showPII = !settings.hidePIIForVendors || user?.userIsAdmin;

	const { enrollments, lastMachinePing } = data ?? {};
	const selectedEnrollment = useMemo(
		() => enrollments?.filter((e) => e.enrollmentID === selected)[0],
		[enrollments, selected],
	);

	const selectItem = useCallback((id: number) => () => props.onSelect(id), [props.onSelect]);
	const hoverItem = useCallback((id: number) => () => props.onHover(id), [props.onHover]);

	if (hasValue(selected)) return renderSelected();

	return (
		<div className={style.list}>
			{lastMachinePing && renderPing()}
			{enrollments?.map(renderEnrollment)}
		</div>
	);

	function renderSelected() {
		return props.selected ? renderSelectedMapSale() : renderSelectedPing();
	}

	//back button, and in overall view, make list width grow if item selected
	function renderSelectedPing() {
		const ping = lastMachinePing;
		return (
			<Row className={style.listItem}>
				<div className={style.itemHeader} onClick={selectItem(null)}>
					<FontAwesomeIcon icon={faArrowLeft} /> Back
				</div>
				<br />
				<div className={style.itemHeader}>Last Machine Ping</div>
				<div className={style.itemBody}>Position: {renderPosition(ping)}</div>
				<div className={style.itemBody}>Timestamp: {ping.timestamp}</div>
			</Row>
		);
	}

	function renderSelectedMapSale() {
		const enrollment = selectedEnrollment;
		if (!enrollment) return null;
		return (
			<Row className={style.listItem}>
				<div className={style.itemHeader} onClick={selectItem(null)}>
					<FontAwesomeIcon icon={faArrowLeft} /> Back
				</div>
				<br />
				<div className={style.itemHeader}>Enrollment Id: {enrollment.enrollmentID}</div>
				<div className={style.itemBody}>Position: {renderPosition(enrollment)}</div>
				<div className={style.itemBody}>Timestamp: {enrollment.timestamp}</div>
				{showPII && (
					<>
						<div className={style.itemBody}>Utility Account Number: {enrollment.utilityAccountNumber}</div>
						<div className={style.itemBody}>Customer Name: {enrollment.customer}</div>
						<div className={style.itemBody}>
							Sales Addresses:{" "}
							{enrollment.serviceAddresses?.length
								? enrollment.serviceAddresses.reduce((a, b) => a + ", " + b)
								: "None"}
						</div>
					</>
				)}
				<div className={style.itemBody}>Meter Count: {enrollment.meterCount}</div>
				<div className={style.itemBody}>Machine Name: {enrollment.machine}</div>
			</Row>
		);
	}

	function renderEnrollment(enrollment: MapSale) {
		const itemStyle = styles(style.listItem, enrollment.enrollmentID === props.hovered && style.hover);
		return (
			<Row
				className={itemStyle}
				onClick={selectItem(enrollment.enrollmentID)}
				key={enrollment.enrollmentID}
				onMouseEnter={hoverItem(enrollment.enrollmentID)}
				onMouseLeave={hoverItem(null)}
			>
				<div className={style.itemHeader}>Enrollment Id: {enrollment.enrollmentID}</div>
				<div className={style.itemBody}>Position: {renderPosition(enrollment)}</div>
				<div className={style.itemBody}>Timestamp: {enrollment.timestamp}</div>
			</Row>
		);
	}

	function renderPosition(enrollment: MapSale) {
		if (!enrollment.lat || !enrollment.lng) return "No GPS Found";
		return (
			<>
				{enrollment.lat}, {enrollment.lng}
			</>
		);
	}

	function renderPing() {
		const ping = lastMachinePing;
		const itemStyle = styles(style.listItem, 0 === props.hovered && style.hover);
		return (
			<Row
				className={itemStyle}
				onClick={selectItem(0)}
				onMouseEnter={hoverItem(0)}
				onMouseLeave={hoverItem(null)}
			>
				<div className={style.itemHeader}>Last Machine Ping</div>
				<div className={style.itemBody}>Position: {renderPosition(ping)}</div>
				<div className={style.itemBody}>Timestamp: {ping.timestamp}</div>
			</Row>
		);
	}
}
